import React, { forwardRef, useEffect, useRef } from "react";
import StarterKit from "@tiptap/starter-kit";
import Highlight from "@tiptap/extension-highlight";
import TextAlign from "@tiptap/extension-text-align";
import Underline from "@tiptap/extension-underline";
import Blockquote from "@tiptap/extension-blockquote";
import Image from "@tiptap/extension-image";
import { EditorContent, useEditor, posToDOMRect } from "@tiptap/react";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Tooltip from "@mui/material/Tooltip";
import FormatBoldIcon from "@mui/icons-material/FormatBold";
import FormatItalicIcon from "@mui/icons-material/FormatItalic";
import FormatUnderlinedIcon from "@mui/icons-material/FormatUnderlined";
import StrikethroughSIcon from "@mui/icons-material/StrikethroughS";
import TextFormatIcon from "@mui/icons-material/TextFormat";
import FormatQuoteIcon from "@mui/icons-material/FormatQuote";
import Crop32Icon from "@mui/icons-material/Crop32";
import CodeIcon from "@mui/icons-material/Code";
import RemoveIcon from "@mui/icons-material/Remove";
import GridOnIcon from "@mui/icons-material/GridOn";
import FormatClearIcon from "@mui/icons-material/FormatClear";
import SpellcheckIcon from "@mui/icons-material/Spellcheck";
import FormatAlignLeftIcon from "@mui/icons-material/FormatAlignLeft";
import FormatAlignCenterIcon from "@mui/icons-material/FormatAlignCenter";
import FormatAlignRightIcon from "@mui/icons-material/FormatAlignRight";
import FormatListBulletedIcon from "@mui/icons-material/FormatListBulleted";
import FormatListNumberedIcon from "@mui/icons-material/FormatListNumbered";
import UndoIcon from "@mui/icons-material/Undo";
import RedoIcon from "@mui/icons-material/Redo";
import { DropdownButton } from "../atoms/DropdownButton";
import { EmailLineContainer } from "../atoms/EmailLineContainer";
import { Typography } from "@mui/material";
import TextField from "@mui/material/TextField";
import Autocomplete from "@mui/material/Autocomplete";

const outerMailOpen = `<!DOCTYPE html>
<html lang='en' xmlns='http://www.w3.org/1999/xhtml' xmlns:o='urn:schemas-microsoft-com:office:office'>
<head>
	<meta charset='utf-8'>
  <meta name='viewport' content='width=device-width,initial-scale=1'>
  <meta name='x-apple-disable-message-reformatting'>
  <!--[if !mso]><!-->
	  <meta http-equiv='X-UA-Compatible' content='IE=edge' />
	<!--<![endif]-->
  <title></title>
	<!--[if mso]>
	<style type='text/css'>
    table {border-collapse:collapse;border:0;border-spacing:0;margin:0;}
    div, td {padding:0;}
    div {margin:0 !important;}
	</style>
  <noscript>
    <xml>
      <o:OfficeDocumentSettings>
        <o:PixelsPerInch>96</o:PixelsPerInch>
      </o:OfficeDocumentSettings>
    </xml>
  </noscript>
  <![endif]-->
  <style type='text/css'>
    body[data-outlook-cycle] .image{
  width:300px;
}

    @media screen and (max-width: 410px) {
      .two-col .column-left {
        max-width: 100% !important;
      }
      .two-col img {
        /* width: 100% !important; */
      }
       .two-col .column {
        max-width: 50% !important;
      }
    }
    @media screen and (min-width: 411px) {
      .two-col .column {
        max-width: 50% !important;
      }
      .two-col .column-left {
        max-width: 33.33% !important;
      }
      .two-col .column-right {
        max-width: 66.66% !important;
      }
    }
  </style>
</head>
<body style='margin:0;padding:0;word-spacing:normal;background-color:#ffffff;'>
  <div role='article' aria-roledescription='email' lang='en' style='-webkit-text-size-adjust:100%;-ms-text-size-adjust:100%;'>
    <table role='presentation' style='width:100%;border:0;border-spacing:0;'>
      <tr>
        <td align='center'>
          <!--[if mso]>
          <table role='presentation' align='center' style='width:660px;'>
          <tr>
          <td style='padding:20px 0;'>
          <![endif]-->
          <div class='outer' style='width:96%;max-width:720px;margin:20px auto;'><div style='width:65ch;text-align:left;'>`;

const outerMailClose = `</div></div>
          <!--[if mso]>
          </td>
          </tr>
          </table>
          <![endif]-->
        </td>
      </tr>
    </table>
  </div>
</body>
</html>`;

const menuBarStyle = {
  display: "flex",
  gap: "1px",
  height: "45px",
  "& button": {
    width: "fit-content",
    p: "6px",
    border: "none",
    backgroundColor: "transparent",
    "&:hover": { bgcolor: "#e7f2ff" },
  },
  "& button:disabled svg": { opacity: 0.5 },
  "& button:first-of-type": { ml: "-10px" },
  "& svg": {
    height: "18px",
    width: "18px",
    color: "rgba(0,0,0,0.77)",
  },
};

const letterToIcon = (letter) => (
  <span
    style={{
      // marginTop: "-8px",
      paddingLeft: "6px",
      fontSize: "1.1rem",
      fontWeight: 500,
    }}
  >
    {letter}
  </span>
);

const MenuBar = ({ editor, disabled = false }) => {
  const allButton = [
    {
      id: "bold",
      icon: <FormatBoldIcon />,
      onClick: () => editor.chain().focus().toggleBold().run(),
      tooltip: "Bold",
    },
    {
      id: "italic",
      icon: <FormatItalicIcon />,
      onClick: () => editor.chain().focus().toggleItalic().run(),
      tooltip: "Italic",
    },
    {
      id: "underline",
      icon: <FormatUnderlinedIcon />,
      onClick: () => editor.chain().focus().toggleUnderline().run(),
      tooltip: "Underline",
    },
    {
      id: "strikethrough",
      icon: <StrikethroughSIcon />,
      onClick: () => editor.chain().focus().toggleStrike().run(),
      tooltip: "Strike through",
    },
    {
      need: false,
      label: (
        <span
          style={{
            // backgroundColor: "red",
            marginTop: "-4px",
            paddingLeft: "6px",
            fontSize: "1.1rem",
            fontWeight: 500,
          }}
        >
          F
        </span>
      ),
    },
    {
      tooltip: "Text type",
      buttonArray: [
        {
          id: "p",
          icon: letterToIcon("P"),
          onClick: () => editor.chain().focus().setParagraph().run(),
        },
        {
          id: "h3",
          icon: letterToIcon("H3"),
          onClick: () =>
            editor.chain().focus().toggleHeading({ level: 3 }).run(),
        },
        {
          id: "h2",
          icon: letterToIcon("H2"),
          onClick: () =>
            editor.chain().focus().toggleHeading({ level: 2 }).run(),
        },
        {
          id: "h1",
          icon: letterToIcon("H1"),
          onClick: () =>
            editor.chain().focus().toggleHeading({ level: 1 }).run(),
        },
      ],
    },
    { need: false, icon: <TextFormatIcon />, onClick: () => { } },
    {
      tooltip: "Align type",
      buttonArray: [
        {
          id: "left",
          icon: <FormatAlignLeftIcon />,
          onClick: () => editor.chain().focus().setTextAlign("left").run(),
        },
        {
          id: "center",
          icon: <FormatAlignCenterIcon />,
          onClick: () => editor.chain().focus().setTextAlign("center").run(),
        },
        {
          id: "right",
          icon: <FormatAlignRightIcon />,
          onClick: () => editor.chain().focus().setTextAlign("right").run(),
        },
      ],
    },
    {
      tooltip: "List type",
      buttonArray: [
        {
          id: "bulletedList",
          icon: <FormatListBulletedIcon />,
          onClick: () => editor.chain().focus().toggleBulletList().run(),
        },
        {
          id: "numberedList",
          icon: <FormatListNumberedIcon />,
          onClick: () => editor.chain().focus().toggleOrderedList().run(),
        },
      ],
    },
    { need: false, dropdown: true },
    { need: false, dropdown: true },
    { need: false, icon: <FormatClearIcon />, onClick: () => { } },
    { need: false },
    { need: false, icon: <GridOnIcon />, onClick: () => { } },
    {
      id: "horizontalRule",
      icon: <RemoveIcon />,
      onClick: () => editor.chain().focus().setHorizontalRule().run(),
      tooltip: "HorizontalRule",
    },
    { need: false, icon: <CodeIcon />, onClick: () => { } },
    { need: false, icon: <Crop32Icon />, onClick: () => { } },
    {
      id: "blockquote",
      icon: <FormatQuoteIcon />,
      onClick: () => editor.chain().focus().toggleBlockquote().run(),
      tooltip: "Blockquote",
    },
    { need: false, label: <SpellcheckIcon /> },
    {
      id: "undo",
      icon: <UndoIcon />,
      onClick: () => editor.chain().focus().undo().run(),
      disabled: !editor.can().chain().focus().undo().run(),
      tooltip: "Undo",
    },
    {
      id: "redo",
      icon: <RedoIcon />,
      onClick: () => editor.chain().focus().redo().run(),
      disabled: !editor.can().chain().focus().redo().run(),
      tooltip: "Redo",
    },
  ];

  if (!editor) {
    return null;
  }

  return (
    <Box sx={menuBarStyle}>
      {allButton
        ?.filter((el) => !(el.need === false))
        ?.map(({ id, icon, need, tooltip, buttonArray, ...rest }) => {
          // if (need === false) return null;
          return (
            <>
              {buttonArray ? (
                <DropdownButton
                  key={id}
                  editor={editor}
                  buttonArray={buttonArray}
                  tooltip={tooltip}
                  disabled={disabled}
                  {...rest}
                />
              ) : null}
              {buttonArray ? null : (
                <Tooltip key={id} title={tooltip} placement="bottom-start">
                  <button type="button" {...rest} disabled={disabled}>
                    {icon}
                  </button>
                </Tooltip>
              )}
            </>
          );
        })}
    </Box>
  );
};

export const TipTap = forwardRef(
  (
    {
      content,
      setEmailContent,
      fieldNameApis,
      signatureComp,
      //------
      popoverWidth = 280,
      recordDataKeys,
      editable = true,
    },
    ref
  ) => {
    const [openPopover, setOpenPopover] = React.useState(false);
    const [left, setLeft] = React.useState(0);
    const [top, setTop] = React.useState(0);
    const [startSearchPosition, setStartSearchPosition] = React.useState(null);
    const [endSearchPosition, setEndSearchPosition] = React.useState(null);

    const currentModuleNumber = React.useRef(0);
    const currentFieldNumber = React.useRef(1);
    const popoverRef = React.useRef();

    const materialAutoRef = React.useRef();

    React.useEffect(() => {
      //popover
      function handleClickOutside(event) {
        if (popoverRef.current && !popoverRef.current.contains(event.target)) {


          setOpenPopover(false);
        }
      }
      document.addEventListener("mousedown", handleClickOutside);
      // textRef.current.focus();
      return () => {
        document.removeEventListener("mousedown", handleClickOutside);
      };
    }, []);

    const handlePopoverClose = () => {
      setOpenPopover(false);
      currentFieldNumber.current = 1;
      currentModuleNumber.current = 0;

      popoverRef.current?.scroll(0, 0);
    };

    const editor = useEditor({
      editable,
      content,
      onUpdate({ editor }) {
        if (signatureComp) {
          editor.getHTML() === "<p></p>"
            ? setEmailContent("")
            : setEmailContent(editor.getHTML());
          return;
        }
        const emailBody = outerMailOpen + editor.getHTML() + outerMailClose;
        // editor.getHTML() === "<p></p>"
        //   ? setEmailContent("")
        //   : setEmailContent(emailBody);
        if (
          editor?.state?.selection &&
          editor?.state?.selection?.ranges?.[0]?.$from.pos !== 1 &&
          editor?.state?.selection?.ranges?.[0]?.$from.pos ===
          editor?.state?.selection?.ranges?.[0]?.$to.pos &&
          editor
            .getText()
            .substring(
              editor.state.selection.ranges[0].$to.pos - 2,
              editor.state.selection.ranges[0].$to.pos - 1
            ) === "#"
        ) {

          setOpenPopover(true);
          setStartSearchPosition(editor.state.selection.ranges[0].$from.pos);
          setEndSearchPosition(editor.state.selection.ranges[0].$to.pos);

          const leftPosition = posToDOMRect(
            editor?.view,
            editor?.state?.selection?.ranges?.[0]?.$from.pos,
            editor?.state?.selection?.ranges?.[0]?.$to.pos
          )?.left;

          if (leftPosition + popoverWidth >= document.body.clientWidth) {
            const left = posToDOMRect(
              editor?.view,
              editor?.state?.selection?.ranges?.[0]?.$from.pos,
              editor?.state?.selection?.ranges?.[0]?.$to.pos
            )?.left;
            setLeft(left - popoverWidth + "px");
          } else {
            const left = posToDOMRect(
              editor?.view,
              editor?.state?.selection?.ranges?.[0]?.$from.pos,
              editor?.state?.selection?.ranges?.[0]?.$to.pos
            )?.left;
            setLeft(left + "px");
          }

          const top = posToDOMRect(
            editor?.view,
            editor?.state?.selection?.ranges?.[0]?.$from.pos,
            editor?.state?.selection?.ranges?.[0]?.$to.pos
          )?.top;

          setTop(top + 15 + "px");
          setTimeout(() => {
            materialAutoRef?.current?.click();
          }, 100);
          return;
        }

        //if there is an empty space after # then popup will close
        if (
          editor
            .getText()
            .substring(editor.state.selection.ranges[0].$from.pos - 2)[0] ===
          " " &&
          openPopover
        ) {
          setStartSearchPosition(editor.state.selection.ranges[0].$from.pos);
          setEndSearchPosition(editor.state.selection.ranges[0].$to.pos);

          handlePopoverClose();
        }

        //if caret position is less than the startSearchPosition(this startSearchPosition is responsible for opening popup)
        if (
          editor.state.selection.ranges[0].$to.pos === startSearchPosition ||
          editor.state.selection.ranges[0].$to.pos < startSearchPosition
        ) {
          setEndSearchPosition(null);
          setStartSearchPosition(null);

          handlePopoverClose();
        }
        // onChangeText(editor.getHTML());
        setEmailContent(emailBody);
      },
      extensions: [
        StarterKit,
        Highlight,
        Underline,
        Blockquote,
        Image,
        TextAlign.configure({
          types: ["heading", "paragraph"],
        }),
      ],
    });

    ref.current = () => {
      editor.commands.setContent("<p></p>");
    };

    return (
      <>
        <Box sx={{ p: "0 1px" }}>
          <EmailLineContainer
            sx={{
              boxShadow: "0 6px 6px 0 rgba(32,33,36,.05)",
              py: 0,
              ...(signatureComp ? { borderColor: "white" } : {}),
            }}
          >
            <Box
              sx={{
                display: "flex",
                flexGrow: 1,
                gap: ".5rem",
                justifyContent: "space-between",
                alignItems: "center",
              }}
            >
              <MenuBar
                editor={editor}
                signatureComp={signatureComp}
                disabled={!editable}
              />
              {signatureComp ? null : (
                <Typography
                  sx={{
                    height: "min-content",
                    bgcolor: "rgba(218, 223, 225, 1)",
                    p: ".25rem",
                    borderRadius: "4px",
                  }}
                >
                  Press # (Shift + 3) to get fields.
                </Typography>
              )}
            </Box>
          </EmailLineContainer>
        </Box>

        <Box
          sx={{
            // display: openPopover ? "block" : "none",
            position: "absolute",
            transition: "top 0.02s ease",
            top: top,
            left: left,
            background: "#fff",
            border: "1px solid #dedede",
            boxShadow: "0 0 5px 2px rgb(0 0 0 / 10%)",
            zIndex: 99999,
          }}
        >
          <Box ref={popoverRef}>
            {openPopover && (
              <Autocomplete
                open={true}
                disablePortal
                size="small"
                options={recordDataKeys}
                sx={{ width: popoverWidth }}
                ref={materialAutoRef}
                renderInput={(params) => <TextField {...params} />}
                // getOptionLabel={(value) => value.fieldDisplayLabel}
                // groupBy={(option) => option.lookupDisplayLabel}
                onChange={(e, value) => {
                  editor.commands.insertContentAt(
                    {
                      from:
                        editor?.state?.selection?.ranges?.[0]?.$from.pos - 1,
                      to: editor?.state?.selection?.ranges?.[0]?.$to.pos,
                    },
                    "${!" + value + "}"
                    // "${!" + value.lookupApiName + "." + value.fieldApiName + "}"
                  );
                  setOpenPopover(false);
                  editor.commands.focus(
                    editor?.state?.selection?.ranges?.[0]?.$to.pos
                  );
                }}
              />
            )}
          </Box>
        </Box>

        <Box
          sx={{
            px: "20px",
            py: "4px",
            flexGrow: 1,
            overflowY: "auto",
            "& ul,ol,p,h1,h2,h3": { m: 0 },
            "& div[contenteditable]": { height: "100%" },
            "& div[contenteditable]:focus-visible": { outline: "none" },
            "& blockquote": {
              borderLeft: "3px solid rgb(192, 198, 204)",
              margin: "1.5rem 0",
              paddingLeft: "1rem",
            },
            // bgcolor: "red",
          }}
        >
          <EditorContent editor={editor} style={{ height: "100%" }} />
        </Box>
      </>
    );
  }
);
